import { useEffect, useState } from 'react';
import { observer, inject } from "mobx-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MenuIcon from '@mui/icons-material/Menu';

import Translator from '../locales/translator';
import logo from '../assets/img/logo-xpens.svg';
import Utils from '../utils/utils';
import AccessControl from "../utils/access_control";
import DataStore from '../utils/data-store';

const Menu = inject("data")(observer(({ data }) => {
    const location = useLocation();
    const history = useNavigate();

    const [isMenuOpen, setIsMenuOpen] = useState(!data.isMobile);
    
    useEffect(() => {
        data.isMenuOpen = isMenuOpen;
    }, [isMenuOpen]);

    const isOnExpensesAccountCreationPage = location.pathname.match(/\/expenses_account\/([0-9]|[a-f])+\/?$/);

    const toggleNav = () => {
        setIsMenuOpen(!isMenuOpen);
    };
    const closeMobileMenu = () => {
        if (data.isMobile) {
            setIsMenuOpen(false);
        }
    };

    const updateLang = () => {
        data.t.switchLang();
        data.t = new Translator();
    };

    const signout = () => {
        DataStore.clearUserData(data);
        data.isMenuOpen = false;
        history('/signin', {replace: true});
    };

    return (
        <div className={`menu ${isMenuOpen ? "menu--open" : ""} ${data.isMobile ? "mobile_menu" : "desktop_menu"}`} >
            <div className={data.isMobile ? "mobile_menu_header" : "mt-4 mb-5"} >
                <div>
                    <img style={{ maxWidth: '200px', maxHeight: '60px' }} src={data.config && data.config.logo ? data.config.logo : logo} alt="XPENS logo" />
                </div>
                {data.isMobile && (isOnExpensesAccountCreationPage
                    ? <button className="btn border-0 py-1" onClick={() => history('/expenses_accounts')} data-testid="back-button">{data.t.read("back")}</button>
                    : <div onClick={toggleNav} data-testid="menu-toggle"><MenuIcon/></div>
                )}
            </div>
            {isMenuOpen && <nav>
                <ul className="iconed">
                    {!data.isMobile && <>
                        {
                            !AccessControl.userHasLimitedAccess(data) &&
                            <li onClick={closeMobileMenu}
                                className={`dashboard-link ${location.pathname === "/dashboard" ? "selected" : ""}`}>
                                <Link to="/dashboard">
                                    {data.t.read("dashboard")}
                                </Link>
                            </li>
                        }
                        <li onClick={closeMobileMenu} className={`dashboard-link ${location.pathname === "/archives" ? "selected" : ""}`}>
                            <Link to="/archives">
                                {data.t.read("archives")}
                            </Link>
                        </li>
                        {AccessControl.canAccessReports(data) && (
                            <li onClick={closeMobileMenu} className={`dashboard-link ${location.pathname === "/reports" ? "selected" : ""}`}>
                                <Link to="/reports">
                                    {data.t.read("reports")}
                                </Link>
                            </li>
                        )}</>}
                    {AccessControl.canAccessApprovals(data) && (
                        <li onClick={closeMobileMenu}
                            className={`dashboard-link ${location.pathname === "/approvals" ? "selected" : ""}`}>
                            <Link to="/approvals">
                                {data.t.read("approvals")}
                            </Link>
                        </li>
                    )}
                    {!data.isMobile && AccessControl.userHasPageAccess(data, "payments") && (
                        <div className="dropdown">
                            <li onClick={closeMobileMenu} className={`dropdown-toggle payments-link ${location.pathname.indexOf("/payments") !== -1 ? "selected" : ""}`} type="button" data-toggle="dropdown">
                                {data.t.read("payments")}
                            </li>
                            <div className="dropdown-menu">
                                <Link onClick={closeMobileMenu} className="dropdown-item" to="/payments/invoices">
                                    {data.t.read("invoices")}
                                </Link>
                                <Link onClick={closeMobileMenu} className="dropdown-item" to="/payments/approval">
                                    {data.t.read("approval")}
                                </Link>
                                <Link onClick={closeMobileMenu} className="dropdown-item" to="/payments/processing_account">
                                    {data.t.read("processing_account")}
                                </Link>
                            </div>
                        </div>
                    )}
                    {AccessControl.userHasPageAccess(data, "expenses") && (
                        <div className="dropdown">
                            <li className={`dropdown-toggle data-link ${location.pathname.startsWith("/expenses_") ? "selected" : ""}`} type="button" data-toggle="dropdown">
                                {data.t.read("expenses_account")}
                            </li>
                            <div className="dropdown-menu">
                                <Link onClick={closeMobileMenu} className="dropdown-item" to="/expenses_accounts">
                                    {data.t.read("expenses_account")}
                                </Link>
                                {AccessControl.canAccessExpenseConfigurations(data) && (
                                    <Link onClick={closeMobileMenu} className="dropdown-item" to="/expenses_configuration/imputations">
                                        {data.t.read("configuration")}
                                    </Link>
                                )}
                                {AccessControl.canAccessExpenseRules(data) && (
                                    <Link onClick={closeMobileMenu} className="dropdown-item" to="/expenses_rules">
                                        {data.t.read("rules")}
                                    </Link>
                                )}
                            </div>
                        </div>
                    ) }
                    {AccessControl.userHasPageAccess(data, "c-card") && AccessControl.hasActionAccess(data, "canAccessCreditCardModule") && (
                        <div className="dropdown">
                            <li className={`dropdown-toggle data-link ${location.pathname.startsWith("/c_card") ? "selected" : ""}`} type="button" data-toggle="dropdown">
                                {data.t.read("c-card")}
                            </li>
                            <div className="dropdown-menu">
                                <Link className="dropdown-item" to="/c_card/statements" >
                                    {data.t.read("c-card")}
                                </Link>
                                <Link className="dropdown-item" to="/c_card/reconciliation" onClick={closeMobileMenu}>
                                    {data.t.read("cc_statements.reconciliation")}
                                </Link>
                                <Link className="dropdown-item" to="/c_card/configuration/categories" >
                                    {data.t.read("configuration")}
                                </Link>
                            </div>
                        </div>
                    )}

                    {!data.isMobile && AccessControl.userHasPageAccess(data, "monitor") && (
                        <li onClick={closeMobileMenu} className={`data-link ${location.pathname === "/monitor" ? "selected" : ""}`}>
                            <Link to="/monitor">
                                {data.t.read("monitor")}
                            </Link>
                        </li>
                    )}
					{!data.isMobile && AccessControl.userHasPageAccess(data, "bi") && (
                        <li onClick={closeMobileMenu} className={`data-link ${location.pathname === "/bi" ? "selected" : ""}`}>
                            <Link to="/bi">
                                {data.t.read("bi_dashboard.title")}
                            </Link>
                        </li>
                    )}
                    {!data.isMobile && AccessControl.userHasPageAccess(data, "cashflow") && (
                        <li onClick={closeMobileMenu} className={`data-link ${location.pathname === "/cashflow" ? "selected" : ""}`}>
                            <Link to="/cashflow">
                                {data.t.read("cashflow")}
                            </Link>
                        </li>
                    )}
                    {!data.isMobile && AccessControl.userHasPageAccess(data, "data") && (
                        <li onClick={closeMobileMenu} className={`data-link ${location.pathname === "/data" ? "selected" : ""}`}>
                            <Link to="/data">
                                {data.t.read("data")}
                            </Link>
                        </li>
                    )}
                    {!data.isMobile && AccessControl.userHasPageAccess(data, "workflow") && (
                        <li onClick={closeMobileMenu} className={`workflow-link ${location.pathname === "/workflow" ? "selected" : ""}`}>
                            <Link to="/workflow">
                                {data.t.read("workflow")}
                            </Link>
                        </li>
                    )}
                    {!data.isMobile && <li onClick={closeMobileMenu} className={`settings-link ${location.pathname === "/settings" ? "selected" : ""}`}>
                        <Link to="/settings">
                            {data.t.read("settings")}
                        </Link>
                    </li>}
                </ul>
            </nav>}
            {
                AccessControl.canSeeInvoiceStatusFilter(data) && !data.isMobile && (location.pathname === "/dashboard" || location.pathname === "/archives") && (
                    <div>
                        <h5 className="mt-4 mb-4">
                            {data.t.read("show")}
                        </h5>
                        <nav>
                            <ul>
                                <li className={`pointer ${data.selectedStatus === "All" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "All")}>
                                    <span>{data.t.read("status.all")}{(location.pathname !== "/archives") && ` (${data?.status_counts?.All ?? 0})`}</span>
                                </li>
                                <li className={`pointer ${data.selectedStatus === "Pending" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Pending")}>
                                    <span>{data.t.read("status.pending")}{(location.pathname !== "/archives") &&` (${data?.status_counts?.Pending ?? 0})`}</span>
                                </li>
                                <li className={`pointer ${data.selectedStatus === "Processing" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Processing")}>
                                    <span>{data.t.read("status.processing")}{(location.pathname !== "/archives") &&` (${data?.status_counts?.Processing ?? 0})`}</span>
                                </li>
                                <li className={`pointer ${data.selectedStatus === "Need_Attention" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Need_Attention")}>
                                    <span>{data.t.read("status.need_attention")}{(location.pathname !== "/archives") &&` (${data?.status_counts?.Need_Attention ?? 0})`}</span>
                                </li>
                                <li className={`pointer ${data.selectedStatus === "Completed" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Completed")}>
                                    <span>{data.t.read("status.completed")}{(location.pathname !== "/archives") &&` (${data?.status_counts?.Completed ?? 0})`}</span>
                                </li>
                                {data.config?.invoice_status.indexOf("Need_Approval") !== -1 && (
                                    <li className={`pointer ${data.selectedStatus === "Need_Approval" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Need_Approval")}>
                                        <span>{data.t.read("status.need_approval")}{(location.pathname !== "/archives") &&` (${data?.status_counts?.Need_Approval ?? 0})`}</span>
                                    </li>
                                )}
                                {data.config?.invoice_status.indexOf("Denied") !== -1 && (
                                    <li className={`pointer ${data.selectedStatus === "Denied" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Denied")}>
                                        <span>{data.t.read("status.denied")}{(location.pathname !== "/archives") &&` (${data?.status_counts?.Denied ?? 0})`}</span>
                                    </li>
                                )}
                                {data.config?.invoice_status.indexOf("Approved") !== -1 && (
                                    <li className={`pointer ${data.selectedStatus === "Approved" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Approved")}>
                                        <span>{data.t.read("status.approved")}{(location.pathname !== "/archives") &&` (${data?.status_counts?.Approved ?? 0})`}</span>
                                    </li>
                                )}
                                <li className={`pointer ${data.selectedStatus === "Validated" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Validated")}>
                                    <span>{data.t.read("status.validated")}{(location.pathname !== "/archives") &&` (${data?.status_counts?.Validated ?? 0})`}</span>
                                </li>
                                <li className={`pointer ${data.selectedStatus === "Finalized" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Finalized")}>
                                    <span>{data.t.read("status.finalized")}{(location.pathname !== "/archives") &&` (${data?.status_counts?.Finalized ?? 0})`}</span>
                                </li>
                                <li className={`pointer ${data.selectedStatus === "Invalid" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Invalid")}>
                                    <span>{data.t.read("status.invalid")}{(location.pathname !== "/archives") &&` (${data.status_counts?.Invalid ?? 0})`}</span>
                                </li>
                                <li className={`pointer ${data.selectedStatus === "Deleted" ? "selected" : ""}`} onClick={() => DataStore.setSelectedStatus(data, "Deleted")}>
                                    <span>{data.t.read("status.deleted")}</span>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}

            {isMenuOpen && <div className="mt-auto mb-4">
                <ul>
                    {!data.isMobile && data.role && AccessControl.canAccessAdminShell(data) && (
                        <li onClick={closeMobileMenu} className={`admin-link ${location.pathname === "/admin" ? "selected" : ""}`}>
                            <Link to={AccessControl.getFirstAdminSectionPath(data)}>
                                Administration
                            </Link>
                        </li>
                    )}
                    <li className="pointer" onClick={() => { updateLang(); closeMobileMenu(); }}>
                        {data.t.lang === "fr" ? (
                            <span><strong>Français</strong> / English</span>
                        ) : (
                            <span>Français / <strong>English</strong></span>
                        )}
                    </li>
                    <li className="pointer" onClick={() => signout()}>
                        {data.t.read("signout")}
                    </li>
                </ul>
            </div>}
        </div>
    );
}));

export default Menu;
